/**
 * FindingsTimeline -- Severity-grouped timeline with rail lines
 *
 * Groups findings by severity (critical → major → minor → observation),
 * renders vertical rail lines with connector dots, and animates card entrance.
 */

import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import type { Id } from "@/packages/types";
import type {
  SeverityType,
  FindingStatusType,
} from "@/packages/domains/findings";
import { springs } from "@/components/ui/motion";
import {
  useSeverityConfig,
  SEVERITY_ORDER,
  SEVERITY_COLORS,
} from "./findings-constants";
import { FindingsTimelineCard } from "./FindingsTimelineCard";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FindingDoc {
  _id: Id<"findings">;
  title: string;
  observation: string;
  recommendation: string;
  severity: SeverityType;
  status: FindingStatusType;
  categoryId: Id<"checklistCategories">;
  checklistItemId: Id<"checklistItems">;
}

interface FindingsTimelineProps {
  findings: FindingDoc[];
  categoryMap: Map<string, string>;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FindingsTimeline({
  findings,
  categoryMap,
}: FindingsTimelineProps) {
  const [editingFindingId, setEditingFindingId] = useState<string | null>(null);
  const severityConfig = useSeverityConfig();

  const groups = useMemo(() => {
    const map = new Map<SeverityType, FindingDoc[]>();
    for (const finding of findings) {
      const group = map.get(finding.severity) ?? [];
      group.push(finding);
      map.set(finding.severity, group);
    }
    return SEVERITY_ORDER.reduce<
      { severity: SeverityType; findings: FindingDoc[] }[]
    >((acc, severity) => {
      const group = map.get(severity);
      if (group) acc.push({ severity, findings: group });
      return acc;
    }, []);
  }, [findings]);

  if (groups.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        No findings match the current filters.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {groups.map(({ severity, findings: groupFindings }) => {
        const config = severityConfig[severity];
        const dotColor = SEVERITY_COLORS[severity].dot;

        return (
          <div key={severity}>
            {/* Group header */}
            <div className="flex items-center gap-2.5 mb-3">
              <div
                className="w-[18px] h-[18px] rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${dotColor}20` }}
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: dotColor }}
                />
              </div>
              <span className="text-sm font-semibold">{config.label}</span>
              <span className="text-xs text-muted-foreground">
                {groupFindings.length}{" "}
                {groupFindings.length === 1 ? "finding" : "findings"}
              </span>
            </div>

            {/* Timeline rail + cards */}
            <div className="relative ml-[9px] pl-5">
              {/* Vertical rail line */}
              <motion.div
                className="absolute left-0 top-0 bottom-0 w-0.5 origin-top"
                style={{ backgroundColor: `${dotColor}40` }}
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={springs.smooth}
              />

              {/* Cards */}
              <div className="space-y-3">
                {groupFindings.map((finding, index) => {
                  const categoryName =
                    categoryMap.get(finding.categoryId) ?? "Unknown Section";
                  return (
                    <div key={finding._id} className="relative">
                      {/* Horizontal connector line */}
                      <div
                        className="absolute left-[-20px] top-5 w-4 h-0.5"
                        style={{ backgroundColor: `${dotColor}4D` }}
                      />

                      <motion.div
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          ...springs.smooth,
                          delay: Math.min(index * 0.04, 0.3),
                        }}
                      >
                        <FindingsTimelineCard
                          finding={finding}
                          categoryName={categoryName}
                          isEditing={editingFindingId === finding._id}
                          onEdit={() => setEditingFindingId(finding._id)}
                          onEditSave={() => setEditingFindingId(null)}
                          onEditCancel={() => setEditingFindingId(null)}
                          checklistItemId={finding.checklistItemId}
                        />
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
